import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { RestartAltRounded } from '@mui/icons-material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import {
  courseTemplateApi,
  type SyllabusRegenerationPreviewDto,
} from '../../../lib/api/course-template.api';
import { useTenantStore } from '../../../stores/tenant.store';
import { errorText } from '../../../lib/error-text';
import { RegenerationPreview, fitNeedsConfirmation } from './RegenerationPreview';

interface Props {
  sectionId: string;
  /** Optional human label so the dialog can show what gets re-synced. */
  label?: string;
  /** Called after a successful regeneration so the parent can refresh. */
  onResynced?: (stats: ResyncStats) => void;
}

interface ResyncStats {
  modulesCreated: number;
  modulesUpdated: number;
  itemsCreated: number;
  pagesCreated: number;
  pagesUpdated: number;
  /** Pages a human had edited — reported so the re-sync does not stay silent. */
  pagesKeptOverridden: string[];
  sessionsCreated: number;
  sessionsUpdated: number;
  sessionsRemoved: number;
  sessionsKeptProtected: number;
  sessionsKeyTextsUpdated: number;
  sessionsRemovedOnAttendanceDay: number;
  sessionsSkippedOnAttendanceDate: number;
  assignmentsCreated: number;
  assignmentsUpdated: number;
}

type SyllabusModality = 'in-person' | 'online' | 'hybrid';

interface SyllabusSnapshot {
  templateId: string;
  instructor: { personId?: string; name: string; credentials?: string };
  termRange: { startDate: string; endDate: string };
  modality?: SyllabusModality;
  biblicalVersion?: string;
}

// ─── Re-sincronizar el syllabus de una sección con su plantilla ─────────────
// S5: volver a aplicar la plantilla deja la sección IGUAL a la plantilla. Antes
// de confirmar se ve qué pasa con cada sesión (RegenerationPreview); lo hecho a
// mano en la sección (asistencia, tema, fecha movida, módulo, sesión quitada)
// solo se toca marcando la casilla.

export function ResyncSyllabusButton({ sectionId, label, onResynced }: Props): JSX.Element {
  const { t } = useTranslation('sis');
  const k = 'templateEditor.resync';
  const { currentTenantId } = useTenantStore();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<ResyncStats | null>(null);
  const [snap, setSnap] = useState<SyllabusSnapshot | null>(null);
  const [preview, setPreview] = useState<SyllabusRegenerationPreviewDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirmProtected, setConfirmProtected] = useState(false);
  // S6·2: sesiones recortadas a menos de la mitad o que no caben.
  const [confirmFit, setConfirmFit] = useState(false);

  // The Section doc does NOT carry a syllabusId field; syllabi are stored in
  // `tenants/{t}/syllabi` and indexed by sectionId.
  const loadSnapshot = async (): Promise<SyllabusSnapshot> => {
    const result = await getDocs(
      query(
        collection(db, `tenants/${currentTenantId}/syllabi`),
        where('sectionId', '==', sectionId),
        limit(1),
      ),
    );
    if (result.empty) throw new Error(t(`${k}.noSyllabus`));
    const syll = result.docs[0]!.data() as Record<string, unknown>;
    const s: SyllabusSnapshot = {
      templateId: syll['templateId'] as string,
      instructor: syll['instructor'] as SyllabusSnapshot['instructor'],
      termRange: syll['termRange'] as SyllabusSnapshot['termRange'],
      ...(syll['modality'] ? { modality: syll['modality'] as SyllabusModality } : {}),
      ...(syll['biblicalVersion'] ? { biblicalVersion: syll['biblicalVersion'] as string } : {}),
    };
    if (!s.templateId) throw new Error(t(`${k}.noTemplate`));
    return s;
  };

  const openDialog = async (): Promise<void> => {
    setOpen(true);
    setDone(null);
    setError(null);
    setPreview(null);
    setConfirmProtected(false);
    setConfirmFit(false);
    if (!currentTenantId) return;
    setLoading(true);
    try {
      const s = await loadSnapshot();
      setSnap(s);
      setPreview(
        await courseTemplateApi.previewRegeneration({
          tenantId: currentTenantId,
          templateId: s.templateId,
          sectionId,
          termRange: s.termRange,
        }),
      );
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (): Promise<void> => {
    if (!currentTenantId || !snap) return;
    setBusy(true);
    setError(null);
    try {
      const r = await courseTemplateApi.generateSyllabus({
        tenantId: currentTenantId,
        templateId: snap.templateId,
        sectionId,
        instructor: snap.instructor,
        termRange: snap.termRange,
        ...(snap.modality ? { modality: snap.modality } : {}),
        ...(snap.biblicalVersion ? { biblicalVersion: snap.biblicalVersion } : {}),
        ...(confirmProtected ? { confirmProtected: true } : {}),
        ...(confirmFit ? { confirmFitIssues: true } : {}),
      });
      const stats: ResyncStats = {
        modulesCreated: r.modulesCreated,
        modulesUpdated: r.modulesUpdated,
        itemsCreated: r.itemsCreated,
        pagesCreated: r.pagesCreated,
        pagesUpdated: r.pagesUpdated,
        pagesKeptOverridden: r.pagesKeptOverridden ?? [],
        sessionsCreated: r.sessionsCreated,
        sessionsUpdated: r.sessionsUpdated,
        sessionsRemoved: r.sessionsRemoved,
        sessionsKeptProtected: r.sessionsKeptProtected ?? 0,
        sessionsKeyTextsUpdated: r.sessionsKeyTextsUpdated ?? 0,
        sessionsRemovedOnAttendanceDay: r.sessionsRemovedOnAttendanceDay ?? 0,
        sessionsSkippedOnAttendanceDate: r.sessionsSkippedOnAttendanceDate ?? 0,
        assignmentsCreated: r.assignmentsCreated,
        assignmentsUpdated: r.assignmentsUpdated,
      };
      setDone(stats);
      onResynced?.(stats);
    } catch (e) {
      setError(errorText(e, t(`${k}.error`)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Tooltip title={t(`${k}.tooltip`)}>
        <IconButton
          size="small"
          aria-label={t(`${k}.tooltip`)}
          onClick={(e) => {
            (e.currentTarget as HTMLElement).blur();
            void openDialog();
          }}
        >
          <RestartAltRounded sx={{ fontSize: 17 }} />
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={busy ? undefined : () => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t(`${k}.title`)}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {label && (
              <Typography variant="body2" color="text.secondary">
                {t(`${k}.section`, { label })}
              </Typography>
            )}
            <Alert severity="info" variant="outlined">
              {t(`${k}.intro`)}
            </Alert>
            {!done && (
              <RegenerationPreview
                preview={preview}
                loading={loading}
                confirmProtected={confirmProtected}
                onConfirmProtected={setConfirmProtected}
                confirmFit={confirmFit}
                onConfirmFit={setConfirmFit}
              />
            )}
            {error && <Alert severity="error">{error}</Alert>}
            {done && (
              <Alert severity="success">
                {t('templateEditor.regen.result', {
                  updated: done.sessionsUpdated,
                  created: done.sessionsCreated,
                  removed: done.sessionsRemoved,
                  kept: done.sessionsKeptProtected,
                  keyTexts: done.sessionsKeyTextsUpdated,
                  removedAtt: done.sessionsRemovedOnAttendanceDay,
                  skipped: done.sessionsSkippedOnAttendanceDate,
                  modulesCreated: done.modulesCreated,
                  modulesUpdated: done.modulesUpdated,
                  assignmentsCreated: done.assignmentsCreated,
                  assignmentsUpdated: done.assignmentsUpdated,
                })}
              </Alert>
            )}
            {done && done.pagesKeptOverridden.length > 0 && (
              // Saying nothing here would make the count above read as the whole
              // story: two pages deliberately left alone look like two failures.
              <Alert severity="info">
                {t(`${k}.pagesKept`, {
                  count: done.pagesKeptOverridden.length,
                  pages: done.pagesKeptOverridden.join(', '),
                })}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={busy}>
            {done ? t(`${k}.close`) : t(`${k}.cancel`)}
          </Button>
          {!done && (
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={
                busy ||
                loading ||
                !snap ||
                // S6·2: sin la casilla, el servidor lo rechazaría igual.
                (!confirmFit &&
                  (fitNeedsConfirmation(preview).truncated > 0 ||
                    fitNeedsConfirmation(preview).unplaced > 0))
              }
              startIcon={busy ? <CircularProgress size={14} /> : <RestartAltRounded />}
            >
              {busy ? t(`${k}.running`) : t(`${k}.confirm`)}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
